import { type ReactNode, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Activity,
  Archive,
  ArrowLeft,
  Briefcase,
  BriefcaseBusiness,
  Gift,
  HandCoins,
  MoreVertical,
  OctagonAlert,
  Package,
  Pause,
  Pencil,
  Play,
  Search,
  ScanSearch,
  Trash2,
  Undo2,
  UserPlus,
  Users,
  Zap,
} from 'lucide-react'

import { ApiError } from '../../../lib/api'
import { useAuthSession } from '../../auth/session'
import { fetchAgents } from '../../agents/api'
import { fetchProspects, createProspect } from '../../prospects/api'
import { AddProspectMethodDialog } from '../../prospects/components/AddProspectMethodDialog'
import { buildProspectDetailPath } from '../../prospects/paths'
import { KpiCard, KpiCardSkeleton } from '../../dashboard/components/KpiCard'
import { DashboardSectionHeader } from '../../dashboard/components/DashboardSectionHeader'
import { programStatusBadgeClass } from '../../dashboard/utils/semanticBadges'
import {
  activateProgram,
  archiveProgram,
  deleteProgramFromArchive,
  fetchExchangePacks,
  fetchProgram,
  fetchProgramAssignments,
  pauseProgram,
  reactivateProgram,
  suspendProgram,
  syncProgramAssignments,
  updateProgram,
} from '../api'
import { ProgramFormDialog } from '../components/ProgramFormDialog'
import {
  ProgramAssignmentDialog,
  ProgramCashRulesDialog,
  ProgramLifecycleConfirmDialog,
  ProgramRewardPackDialog,
  type ProgramLifecycleAction,
} from '../components/ProgramActionDialogs'
import { PageHeader, PageHeaderToolbar } from '@/components/app/PageHeader'
import { TablePaginationBar } from '@/components/app/TablePaginationBar'
import { useAppBreadcrumbTrail } from '@/layouts/AppShell'
import { AgentAvatarFallback, Avatar, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { avatarSeedForUser } from '@/lib/avatar-fallback'
import { formatAppDate, formatAppDateTime, formatAppNumber, getAppLocale } from '@/lib/locale'
import { cn } from '@/lib/utils'

import type { AssignedAgent, ProgramMutationPayload, ProgramRecord, ProgramStatus } from '@/types/programs'
import type { ProspectPipelineStage, ProspectRecord, ProspectSubmissionStatus } from '@/types/prospects'

function programStatusLabel(
  status: ProgramStatus,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  return t(`programs.status.${status}`)
}

function pipelineStageLabel(
  stage: ProspectPipelineStage,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  return t(`prospects.stages.${stage}`)
}

function submissionLabel(
  status: ProspectSubmissionStatus,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  return t(`prospects.syncStatus.${status}`)
}

function prospectStageBadgeClass(stage: ProspectPipelineStage) {
  if (stage === 'prospect_chaud') return 'border-transparent bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300'
  if (stage === 'prospect_tiede') return 'border-transparent bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300'
  if (stage === 'prospect_froid') return 'border-transparent bg-blue-500/15 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300'
  return 'border-transparent bg-muted text-muted-foreground'
}

function prospectPipelinePresentation(
  prospect: ProspectRecord,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  if (prospect.conversion_status === 'converted') {
    return {
      label: t('prospects.stages.converted'),
      className: 'border-border bg-emerald-500/10 text-emerald-800 dark:text-emerald-300',
    }
  }

  return {
    label: pipelineStageLabel(prospect.pipeline_stage, t),
    className: prospectStageBadgeClass(prospect.pipeline_stage),
  }
}

function submissionBadgeClass(status: ProspectSubmissionStatus) {
  if (status === 'synced') return 'border-transparent bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300'
  if (status === 'pending_sync') return 'border-transparent bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300'
  if (status === 'sync_failed') return 'border-transparent bg-rose-500/15 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300'
  return 'border-transparent bg-muted text-muted-foreground'
}

function exchangeModeConfig(
  program: ProgramRecord,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  if (program.exchange_mode === 'both') {
    return {
      icon: Briefcase,
      label: t('programs.exchangeModes.both'),
      description: t('programs.card.modes.bothDescription'),
      tileClass: 'bg-blue-500 text-white',
      badgeClass: 'border-blue-500/25 bg-blue-500/10 text-blue-800 dark:text-blue-300',
      panelClass: 'border-blue-500/20 bg-blue-500/5',
    }
  }

  if (program.exchange_mode === 'reward') {
    return {
      icon: Gift,
      label: t('programs.exchangeModes.reward'),
      description: t('programs.card.modes.rewardDescription'),
      tileClass: 'bg-amber-500 text-white',
      badgeClass: 'border-amber-500/25 bg-amber-500/10 text-amber-900 dark:text-amber-300',
      panelClass: 'border-amber-500/20 bg-amber-500/5',
    }
  }

  return {
    icon: HandCoins,
    label: t('programs.exchangeModes.cash'),
    description: t('programs.card.modes.cashDescription'),
    tileClass: 'bg-emerald-500 text-white',
    badgeClass: 'border-emerald-500/25 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300',
    panelClass: 'border-emerald-500/20 bg-emerald-500/5',
  }
}

function commissionLabel(
  program: ProgramRecord,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  return t(`programs.card.attribution.${program.commission_type}`)
}

function pointsRuleLabel(
  program: ProgramRecord,
  t: (key: string, options?: Record<string, unknown>) => string,
  locale: string,
) {
  return program.points_per_transaction === null
    ? t('programs.detail.kpi.pointsRuleTiers')
    : t('programs.card.pointsRuleValue', {
        value: program.points_per_transaction.toLocaleString(locale),
      })
}

function cashRuleLabel(
  program: ProgramRecord,
  t: (key: string, options?: Record<string, unknown>) => string,
  locale: string,
) {
  return program.points_per_euro === null
    ? t('programs.detail.cashNotConfigured')
    : t('programs.card.cashRuleValue', {
        value: program.points_per_euro.toLocaleString(locale),
      })
}

function stepperIndex(status: ProgramStatus): number {
  switch (status) {
    case 'draft':
      return 0
    case 'active':
      return 1
    case 'paused':
    case 'suspended':
      return 2
    case 'archived':
      return 3
    default:
      return 0
  }
}

function stepperTone(status: ProgramStatus): 'primary' | 'success' | 'warning' | 'danger' {
  switch (status) {
    case 'active':
      return 'success'
    case 'paused':
      return 'warning'
    case 'suspended':
      return 'danger'
    case 'archived':
      return 'primary'
    default:
      return 'primary'
  }
}

interface ProgramStatusStepperProps {
  program: ProgramRecord
  t: (key: string, options?: Record<string, unknown>) => string
}

function ProgramStatusStepper({ program, t }: ProgramStatusStepperProps) {
  const steps = [
    { key: 'draft', label: t('programs.detail.stepper.draft'), date: null },
    { key: 'active', label: t('programs.detail.stepper.active'), date: program.activated_at },
    {
      key: 'paused',
      label: program.status === 'suspended' ? t('programs.detail.stepper.suspended') : t('programs.detail.stepper.paused'),
      date: program.suspended_at ?? program.paused_at,
    },
    { key: 'archived', label: t('programs.detail.stepper.archived'), date: null },
  ]
  const currentIndex = stepperIndex(program.status)
  const tone = stepperTone(program.status)

  const toneClasses: Record<string, string> = {
    primary: 'bg-primary text-primary-foreground border-primary',
    success: 'bg-emerald-500 text-white border-emerald-500',
    warning: 'bg-amber-500 text-white border-amber-500',
    danger: 'bg-rose-500 text-white border-rose-500',
  }

  return (
    <div className="flex items-start justify-between gap-2">
      {steps.map((step, index) => {
        const isActive = index === currentIndex
        const isCompleted = index < currentIndex
        return (
          <div key={step.key} className="flex flex-1 flex-col items-center gap-2">
            <div
              className={cn(
                'flex size-10 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors',
                isActive ? toneClasses[tone] : isCompleted ? 'bg-muted text-muted-foreground border-muted' : 'bg-card text-muted-foreground border-border',
              )}
            >
              {index + 1}
            </div>
            <div className="text-center">
              <p
                className={cn(
                  'text-xs font-medium',
                  isActive ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {step.label}
              </p>
              {isActive && step.date ? (
                <p className="mt-0.5 text-[10px] text-muted-foreground">
                  {formatAppDate(step.date)}
                </p>
              ) : null}
              {isActive && program.status === 'suspended' && typeof program.suspension_days_left === 'number' && program.suspension_days_left > 0 ? (
                <Badge variant="secondary" className="mt-1 text-[10px]">
                  {t('programs.detail.stepper.suspensionCountdown', { days: program.suspension_days_left })}
                </Badge>
              ) : null}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function toProgramPayload(
  program: ProgramRecord,
  overrides: Partial<ProgramMutationPayload> = {},
): ProgramMutationPayload {
  return {
    name: program.name,
    description: program.description ?? '',
    commission_type: program.commission_type,
    exchange_mode: program.exchange_mode,
    points_per_transaction: program.points_per_transaction,
    points_per_euro: program.points_per_euro,
    exchange_pack_id: program.exchange_pack?.id ?? null,
    eligibility_criteria: program.eligibility_criteria ?? '',
    status: program.status,
    ...overrides,
  }
}

function agentInitials(displayName: string | null | undefined, email: string | null | undefined) {
  const name = displayName?.trim()
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean)
    if (parts.length > 1) return `${parts[0]![0] ?? ''}${parts[parts.length - 1]![0] ?? ''}`.toUpperCase()
    return name.slice(0, 2).toUpperCase()
  }
  return (email ?? '?').slice(0, 2).toUpperCase()
}

function sortBySubmittedAt(left: ProspectRecord, right: ProspectRecord) {
  return new Date(right.submitted_at ?? 0).getTime() - new Date(left.submitted_at ?? 0).getTime()
}

function ProgramDetailSkeleton({ isAgentView = false }: { isAgentView?: boolean }) {
  return (
    <section className="app-section">
      {/* Header */}
      <div className="flex flex-col gap-2 pb-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div className="flex items-center gap-2">
          <Skeleton className="size-7 rounded-lg" />
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Skeleton className="h-7 w-44" />
              <Skeleton className="h-5 w-20 rounded-full" />
            </div>
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
        <div className="flex justify-end">
          <Skeleton className="h-8 w-28" />
        </div>
      </div>

      {/* Status Stepper */}
      <Card className="border-0 shadow-none">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="flex flex-1 flex-col items-center gap-2">
                <Skeleton className="size-10 rounded-full" />
                <Skeleton className="h-3 w-16" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <KpiCardSkeleton key={index} />
        ))}
      </div>

      {/* Program Info */}
      <Card className="border-0 shadow-none">
        <CardContent className="space-y-5 p-4">
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Skeleton className="h-16 rounded-lg" />
            <Skeleton className="h-16 rounded-lg" />
          </div>
          <Skeleton className="h-20 rounded-lg" />
          <div className="grid gap-3 sm:grid-cols-2">
            <Skeleton className="h-24 rounded-lg" />
            <Skeleton className="h-24 rounded-lg" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-8 w-32 rounded-full" />
            <Skeleton className="h-8 w-32 rounded-full" />
          </div>
        </CardContent>
      </Card>

      {/* Agents Table (owner only) */}
      {!isAgentView ? (
        <Card className="border-0 shadow-none">
          <CardContent className="p-4">
            <Skeleton className="mb-4 h-5 w-40" />
            <Skeleton className="mb-3 h-8 w-64" />
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="h-10 bg-muted/30" />
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3',
                    index % 2 === 1 && 'bg-muted/15',
                    index > 0 && 'border-t border-border/50',
                  )}
                >
                  <Skeleton className="h-4 w-6 rounded-md" />
                  <Skeleton className="h-4 w-32 rounded-md" />
                  <Skeleton className="hidden h-4 w-40 rounded-md sm:block" />
                  <Skeleton className="hidden h-4 w-20 rounded-md md:block" />
                  <Skeleton className="h-5 w-20 rounded-full" />
                  <Skeleton className="ml-auto hidden h-4 w-24 rounded-md lg:block" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Prospects Table */}
      <Card className="border-0 shadow-none">
        <CardContent className="p-4">
          <Skeleton className="mb-4 h-5 w-40" />
          <Skeleton className="mb-3 h-8 w-64" />
          <div className="overflow-hidden rounded-lg border border-border">
            <div className="h-10 bg-muted/30" />
            {Array.from({ length: 5 }).map((_, index) => (
              <div
                key={index}
                className={cn(
                  'flex items-center gap-3 px-4 py-3',
                  index % 2 === 1 && 'bg-muted/15',
                  index > 0 && 'border-t border-border/50',
                )}
              >
                <Skeleton className="h-4 w-6 rounded-md" />
                <Skeleton className="h-4 w-32 rounded-md" />
                <Skeleton className="hidden h-4 w-32 rounded-md sm:block" />
                {!isAgentView ? (
                  <>
                    <Skeleton className="hidden h-4 w-28 rounded-md md:block" />
                    <Skeleton className="h-5 w-20 rounded-full" />
                  </>
                ) : null}
                <Skeleton className="h-5 w-20 rounded-full" />
                <Skeleton className="ml-auto hidden h-4 w-24 rounded-md lg:block" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </section>
  )
}

export function ProgramDetailPage() {
  const { t } = useTranslation()
  const locale = getAppLocale()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { programId } = useParams<{ programId: string }>()
  const { hasPermission, user } = useAuthSession()
  const [editOpen, setEditOpen] = useState(false)
  const [cashEditOpen, setCashEditOpen] = useState(false)
  const [rewardsEditOpen, setRewardsEditOpen] = useState(false)
  const [addProspectMethodOpen, setAddProspectMethodOpen] = useState(false)
  const [assignDialogProgram, setAssignDialogProgram] = useState<ProgramRecord | null>(null)
  const [agentTableSearch, setAgentTableSearch] = useState('')
  const [agentTablePage, setAgentTablePage] = useState(1)
  const [agentTablePageSize, setAgentTablePageSize] = useState(10)
  const [prospectTableSearch, setProspectTableSearch] = useState('')
  const [prospectTablePage, setProspectTablePage] = useState(1)
  const [prospectTablePageSize, setProspectTablePageSize] = useState(10)
  const [pendingOwnerAction, setPendingOwnerAction] = useState<{
    type: ProgramLifecycleAction
    program: ProgramRecord
  } | null>(null)
  const canViewProspects = hasPermission('prospect.view')
  const cardMode = user?.agent_profile !== null ? 'agent' : 'owner'
  const isAgentView = Boolean(user?.agent_profile)

  const programQuery = useQuery({
    queryKey: ['programs', 'detail', programId],
    queryFn: async () => fetchProgram(programId ?? ''),
    enabled: Boolean(programId),
  })

  const prospectsQuery = useQuery({
    queryKey: ['prospects', 'list', 'program-detail', programId],
    queryFn: fetchProspects,
    enabled: Boolean(programId) && canViewProspects,
  })

  const packsQuery = useQuery({
    queryKey: ['exchange-packs', 'list', 'program-detail'],
    queryFn: () => fetchExchangePacks(),
    enabled: (editOpen || rewardsEditOpen) && hasPermission('exchange-pack.view'),
  })

  const agentsQuery = useQuery({
    queryKey: ['agents', 'list'],
    queryFn: fetchAgents,
    enabled: Boolean(assignDialogProgram) && hasPermission('agent.view'),
  })

  const assignmentQuery = useQuery({
    queryKey: ['programs', 'assignments', assignDialogProgram?.id],
    queryFn: async () => {
      if (!assignDialogProgram) {
        return { data: [] as AssignedAgent[] }
      }
      return fetchProgramAssignments(assignDialogProgram.id)
    },
    enabled: Boolean(assignDialogProgram?.id),
  })

  const updateMutation = useMutation({
    mutationFn: ({ payload }: { payload: ProgramMutationPayload }) => {
      if (!programId) throw new Error(t('programs.detail.missingId'))
      return updateProgram(programId, payload)
    },
    onSuccess: async () => {
      setEditOpen(false)
      setCashEditOpen(false)
      setRewardsEditOpen(false)
      await queryClient.invalidateQueries({ queryKey: ['programs'] })
    },
  })

  const activateMutation = useMutation({
    mutationFn: activateProgram,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['programs'] })
      await queryClient.invalidateQueries({ queryKey: ['programs', 'detail', programId] })
    },
  })

  const pauseMutation = useMutation({
    mutationFn: pauseProgram,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['programs'] })
      await queryClient.invalidateQueries({ queryKey: ['programs', 'detail', programId] })
    },
  })

  const reactivateMutation = useMutation({
    mutationFn: reactivateProgram,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['programs'] })
      await queryClient.invalidateQueries({ queryKey: ['programs', 'detail', programId] })
    },
  })

  const suspendMutation = useMutation({
    mutationFn: suspendProgram,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['programs'] })
      await queryClient.invalidateQueries({ queryKey: ['programs', 'detail', programId] })
    },
  })

  const archiveMutation = useMutation({
    mutationFn: archiveProgram,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['programs'] })
      await queryClient.invalidateQueries({ queryKey: ['programs', 'detail', programId] })
    },
  })

  const deleteArchivedMutation = useMutation({
    mutationFn: deleteProgramFromArchive,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['programs'] })
      navigate('/programs?scope=archived')
    },
  })

  const syncAssignmentsMutation = useMutation({
    mutationFn: ({ nextProgramId, agentIds }: { nextProgramId: string; agentIds: string[] }) =>
      syncProgramAssignments(nextProgramId, agentIds),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['programs'] })
      await queryClient.invalidateQueries({ queryKey: ['programs', 'detail', programId] })
      if (assignDialogProgram?.id) {
        await queryClient.invalidateQueries({
          queryKey: ['programs', 'assignments', assignDialogProgram.id],
        })
      }
      setAssignDialogProgram(null)
    },
  })

  const createProspectMutation = useMutation({
    mutationFn: createProspect,
    onSuccess: async () => {
      setAddProspectMethodOpen(false)
      await queryClient.invalidateQueries({ queryKey: ['prospects'] })
    },
  })

  useAppBreadcrumbTrail(
    programQuery.data?.data
      ? [
          { label: t('navigation.programs'), to: '/programs' },
          { label: programQuery.data.data.name },
        ]
      : null,
  )

  if (!programId) {
    return (
      <article className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {t('programs.detail.missingId')}
      </article>
    )
  }

  if (programQuery.isPending) {
    return <ProgramDetailSkeleton isAgentView={isAgentView} />
  }

  if (programQuery.isError) {
    return (
      <article className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {(programQuery.error as ApiError).message}
      </article>
    )
  }

  const program = programQuery.data.data
  const assignedAgents = (program.assigned_agents ?? [])
    .filter((assignment) => assignment.agent !== null)
  const programProspects = (prospectsQuery.data?.data ?? [])
    .filter((prospect) => prospect.program_id === program.id)
    .sort(sortBySubmittedAt)
  const agentSearch = agentTableSearch.trim().toLowerCase()
  const filteredAssignedAgents = assignedAgents.filter((assignment) => {
    if (agentSearch.length === 0) return true
    const agent = assignment.agent
    return (
      (agent?.display_name ?? '').toLowerCase().includes(agentSearch) ||
      (agent?.email ?? '').toLowerCase().includes(agentSearch) ||
      (agent?.agent_code ?? '').toLowerCase().includes(agentSearch) ||
      assignment.status.toLowerCase().includes(agentSearch)
    )
  })
  const agentTotalPages = Math.max(1, Math.ceil(filteredAssignedAgents.length / agentTablePageSize))
  const agentPageSafe = Math.min(agentTablePage, agentTotalPages)
  const pagedAssignedAgents = filteredAssignedAgents.slice(
    (agentPageSafe - 1) * agentTablePageSize,
    (agentPageSafe - 1) * agentTablePageSize + agentTablePageSize,
  )
  const prospectSearch = prospectTableSearch.trim().toLowerCase()
  const filteredProgramProspects = programProspects.filter((prospect) => {
    if (prospectSearch.length === 0) return true
    return (
      prospect.contact_name.toLowerCase().includes(prospectSearch) ||
      (prospect.company_name ?? '').toLowerCase().includes(prospectSearch) ||
      (prospect.contact_email ?? '').toLowerCase().includes(prospectSearch) ||
      (prospect.agent_name ?? '').toLowerCase().includes(prospectSearch) ||
      prospect.pipeline_stage.toLowerCase().includes(prospectSearch) ||
      prospect.submission_status.toLowerCase().includes(prospectSearch)
    )
  })
  const prospectTotalPages = Math.max(1, Math.ceil(filteredProgramProspects.length / prospectTablePageSize))
  const prospectPageSafe = Math.min(prospectTablePage, prospectTotalPages)
  const pagedProgramProspects = filteredProgramProspects.slice(
    (prospectPageSafe - 1) * prospectTablePageSize,
    (prospectPageSafe - 1) * prospectTablePageSize + prospectTablePageSize,
  )
  const activeProspects = programProspects.filter((prospect) => prospect.deleted_at === null)
  const convertedProspects = activeProspects.filter((prospect) => prospect.conversion_status === 'converted')
  const myAgentId = user?.agent_profile?.id
  const myProspects = activeProspects.filter((prospect) => prospect.agent_id === myAgentId)
  const myConverted = myProspects.filter((prospect) => prospect.conversion_status === 'converted')
  const exchangeConfig = exchangeModeConfig(program, t)
  const rewardPacks = (packsQuery.data?.data ?? []).filter((pack) => pack.status === 'active')
  const canEditProgram = Boolean(program.actions.can_edit_general ?? program.actions.can_update) && hasPermission('program.update')
  const canSubmitProspect = cardMode === 'agent' && hasPermission('prospect.submit') && program.status === 'active'
  const createProspectError = createProspectMutation.error as ApiError | null
  const updateProgramError = updateMutation.error as ApiError | null
  const isPaused = program.status === 'paused'
  const isSuspended = program.status === 'suspended'
  const isDraft = program.status === 'draft'
  const isRevenueTier = program.commission_type === 'revenue-tier'
  const hasCash = program.exchange_mode === 'cash' || program.exchange_mode === 'both'
  const hasRewards = program.exchange_mode === 'reward' || program.exchange_mode === 'both'
  const assignedTotal = program.assigned_agents_count ?? assignedAgents.length
  const isOwnerActionPending =
    pauseMutation.isPending ||
    reactivateMutation.isPending ||
    suspendMutation.isPending ||
    archiveMutation.isPending ||
    deleteArchivedMutation.isPending
  const canEditCashShortcut = Boolean(hasCash && program.actions.can_edit_cash && canEditProgram)
  const canEditRewardsShortcut = Boolean(hasRewards && program.actions.can_edit_rewards && canEditProgram)
  const canActivateProgram = Boolean(program.actions.can_activate && program.actions.can_update)
  const canTogglePauseAction = Boolean((program.actions.can_pause || program.actions.can_reactivate) && !isOwnerActionPending)
  const pauseDisabled = !canTogglePauseAction || (isPaused && isRevenueTier)
  const canSuspendAction = Boolean(program.actions.can_suspend && !isOwnerActionPending)
  const canLiftSuspensionAction = Boolean(program.actions.can_lift_suspension && !isOwnerActionPending)
  const canArchiveAction = Boolean(program.actions.can_archive && !isOwnerActionPending)
  const canAssignAction = Boolean(
    program.actions.can_assign_agent &&
      hasPermission('program.assign-agent') &&
      !isSuspended &&
      program.status !== 'archived',
  )
  const canDeleteAction = Boolean(
    (program.actions.can_soft_delete ?? program.actions.can_delete_from_archive) && !isOwnerActionPending,
  )
  const canCreateProspect = Boolean(canSubmitProspect && program.status === 'active')
  const editDisabledReason = canEditProgram
    ? null
    : program.status === 'archived'
      ? t('programs.card.tooltip.editArchived')
      : assignedTotal > 0
        ? t('programs.card.tooltip.editAssigned')
        : t('programs.card.tooltip.editFallback')
  const editCashDisabledReason = canEditCashShortcut
    ? null
    : !hasCash
      ? t('programs.card.tooltip.cashNoMode')
      : program.status === 'archived'
        ? t('programs.card.tooltip.cashArchived')
        : assignedTotal > 0
          ? t('programs.card.tooltip.cashAssigned')
          : t('programs.card.tooltip.cashFallback')
  const editRewardsDisabledReason = canEditRewardsShortcut
    ? null
    : !hasRewards
      ? t('programs.card.tooltip.rewardsNoMode')
      : program.status === 'archived'
        ? t('programs.card.tooltip.rewardsArchived')
        : t('programs.card.tooltip.rewardsFallback')
  const activateDisabledReason = canActivateProgram
    ? null
    : program.status !== 'draft'
      ? t('programs.card.tooltip.activateNotDraft')
      : t('programs.card.tooltip.activateFallback')
  const liftSuspensionDisabledReason = canLiftSuspensionAction
    ? null
    : program.status !== 'suspended'
      ? t('programs.card.tooltip.liftNotSuspended')
      : t('programs.card.tooltip.liftFallback')
  const pauseDisabledReason = pauseDisabled
    ? isOwnerActionPending
      ? t('programs.card.tooltip.pausePending')
      : isPaused && isRevenueTier
        ? t('programs.card.tooltip.pauseRevenueTier')
        : isPaused
                        ? t('programs.card.tooltip.pauseReactivate')
          : t('programs.card.tooltip.pauseFallback')
    : null
  const suspendDisabledReason = canSuspendAction
    ? null
    : program.status !== 'active' && program.status !== 'paused'
      ? t('programs.card.tooltip.suspendInvalidStatus')
      : program.has_open_prospects
        ? t('programs.card.tooltip.suspendOpenProspects')
        : t('programs.card.tooltip.suspendFallback')
  const archiveDisabledReason = canArchiveAction
    ? null
    : program.status !== 'suspended'
      ? t('programs.card.tooltip.archiveInvalidStatus')
      : !program.suspension_deadline_at
        ? t('programs.card.tooltip.archiveNoDeadline')
        : t('programs.card.tooltip.archiveFallback')
  const assignDisabledReason = canAssignAction
    ? null
    : isSuspended || program.status === 'archived'
      ? t('programs.card.tooltip.assignBlocked')
      : t('programs.card.tooltip.assignFallback')
  const deleteDisabledReason = canDeleteAction
    ? null
    : program.status === 'archived'
      ? t('programs.card.tooltip.deleteArchived')
      : assignedTotal > 0
        ? t('programs.card.tooltip.deleteAssigned')
        : t('programs.card.tooltip.deleteFallback')
  const addProspectDisabledReason = canCreateProspect
    ? null
    : !hasPermission('prospect.submit')
      ? t('programs.card.tooltip.prospectPermission')
      : program.status !== 'active'
        ? t('programs.card.tooltip.prospectInactive')
        : t('programs.card.tooltip.prospectFallback')

  function withDisabledTooltip(item: ReactNode, reason: string | null) {
    if (!reason) return item
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="block">{item}</div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{reason}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <section className="app-section">
      <PageHeader
        title={program.name}
        beforeTitle={
          <Button type="button" variant="ghost" size="icon-sm" className="-ml-1" asChild>
            <Link to="/programs" aria-label={t('programs.detail.backToPrograms')}>
              <ArrowLeft className="size-4" aria-hidden />
            </Link>
          </Button>
        }
        titleAddon={
          <Badge
            variant="outline"
            className={cn('shrink-0 uppercase tracking-wide', programStatusBadgeClass(program.status))}
          >
            {programStatusLabel(program.status, t)}
          </Badge>
        }
        right={
          <TooltipProvider>
            <PageHeaderToolbar>
              {cardMode === 'agent'
                ? withDisabledTooltip(
                    <Button
                      type="button"
                      size="sm"
                      disabled={!canCreateProspect}
                      onClick={() => {
                        if (canCreateProspect) setAddProspectMethodOpen(true)
                      }}
                    >
                      <Zap className="size-4" aria-hidden />
                      {t('programs.detail.addProspect')}
                    </Button>,
                    addProspectDisabledReason,
                  )
                : null}
              {cardMode === 'owner'
                ? (
                    <>
                      {withDisabledTooltip(
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={!canEditProgram}
                          onClick={() => {
                            if (canEditProgram) setEditOpen(true)
                          }}
                        >
                          <Pencil className="size-4" aria-hidden />
                          {t('programs.detail.edit')}
                        </Button>,
                        editDisabledReason,
                      )}
                      {hasRewards
                        ? withDisabledTooltip(
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={!canEditRewardsShortcut}
                              onClick={() => {
                                if (!canEditRewardsShortcut) return
                                setRewardsEditOpen(true)
                              }}
                            >
                              <Package className="size-4" aria-hidden />
                              {t('programs.detail.manageRewards')}
                            </Button>,
                            editRewardsDisabledReason,
                          )
                        : null}
                      {!isDraft && !isSuspended
                        ? withDisabledTooltip(
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={pauseDisabled}
                              onClick={() => {
                                if (pauseDisabled) return
                                setPendingOwnerAction({
                                  type: isPaused ? 'reactivate' : 'pause',
                                  program,
                                })
                              }}
                            >
                              {isPaused ? <Play className="size-4" aria-hidden /> : <Pause className="size-4" aria-hidden />}
                              {isPaused ? t('programs.detail.reactivate') : t('programs.detail.pause')}
                            </Button>,
                            pauseDisabledReason,
                          )
                        : null}
                      {withDisabledTooltip(
                        <Button
                          type="button"
                          size="sm"
                          disabled={!canAssignAction}
                        onClick={() => {
                          if (!canAssignAction) return
                          setAssignDialogProgram(program)
                          syncAssignmentsMutation.reset()
                        }}
                      >
                          <UserPlus className="size-4" aria-hidden />
                          {t('programs.detail.assignAgents')}
                        </Button>,
                        assignDisabledReason,
                      )}
                    </>
                  )
                : null}
            {cardMode === 'owner' ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline" size="icon-sm" aria-label={t('programs.detail.moreActions')}>
                    <MoreVertical className="size-4" aria-hidden />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <>
                    {hasCash
                      ? withDisabledTooltip(
                          <DropdownMenuItem
                            disabled={!canEditCashShortcut}
                            onSelect={() => {
                              if (!canEditCashShortcut) return
                              setCashEditOpen(true)
                            }}
                          >
                            <HandCoins className="size-4" />
                            {t('programs.detail.editCash')}
                          </DropdownMenuItem>,
                          editCashDisabledReason,
                        )
                      : null}
                    {isDraft && program.actions.can_update
                      ? withDisabledTooltip(
                          <DropdownMenuItem
                            disabled={!canActivateProgram}
                            onSelect={() => {
                              if (canActivateProgram) setPendingOwnerAction({ type: 'activate', program })
                            }}
                          >
                            <Zap className="size-4" />
                            {t('programs.detail.activate')}
                          </DropdownMenuItem>,
                          activateDisabledReason,
                        )
                      : null}
                    {isSuspended ? (
                      withDisabledTooltip(
                        <DropdownMenuItem
                          disabled={!canLiftSuspensionAction}
                          onSelect={() => {
                            if (canLiftSuspensionAction) setPendingOwnerAction({ type: 'lift_suspension', program })
                          }}
                        >
                          <Undo2 className="size-4" />
                          {t('programs.detail.liftSuspension')}
                        </DropdownMenuItem>,
                        liftSuspensionDisabledReason,
                      )
                    ) : !isDraft ? (
                      withDisabledTooltip(
                        <DropdownMenuItem
                          disabled={!canSuspendAction}
                          onSelect={() => {
                            if (!canSuspendAction) return
                            setPendingOwnerAction({ type: 'suspend', program })
                          }}
                        >
                          <OctagonAlert className="size-4" />
                          {t('programs.detail.suspend')}
                        </DropdownMenuItem>,
                        suspendDisabledReason,
                      )
                    ) : null}
                    {withDisabledTooltip(
                      <DropdownMenuItem
                        disabled={!canArchiveAction}
                        onSelect={() => {
                          if (!canArchiveAction) return
                          setPendingOwnerAction({ type: 'archive', program })
                        }}
                      >
                        <Archive className="size-4" />
                        {t('programs.detail.archive')}
                      </DropdownMenuItem>,
                      archiveDisabledReason,
                    )}
                    {withDisabledTooltip(
                      <DropdownMenuItem
                        variant="destructive"
                        disabled={!canDeleteAction}
                        onSelect={() => {
                          if (!canDeleteAction) return
                          setPendingOwnerAction({ type: 'delete', program })
                        }}
                      >
                        <Trash2 className="size-4" />
                        {t('programs.detail.delete')}
                      </DropdownMenuItem>,
                      deleteDisabledReason,
                    )}
                  </>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
            </PageHeaderToolbar>
          </TooltipProvider>
        }
      />

      {/* ── Status Stepper ── */}
      {program.status !== 'draft' && program.status !== 'active' ? (
        <Card className="border-0 shadow-none">
          <CardContent className="p-4">
            <ProgramStatusStepper program={program} t={t} />
          </CardContent>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {isAgentView ? (
          <>
            <KpiCard
              title={t('programs.detail.kpi.myProspects')}
              value={formatAppNumber(myProspects.length)}
              description={t('programs.detail.kpi.myProspectsDescription')}
              icon={ScanSearch}
              tone="primary"
            />
            <KpiCard
              title={t('programs.detail.kpi.myConverted')}
              value={formatAppNumber(myConverted.length)}
              description={t('programs.detail.kpi.myConvertedDescription')}
              icon={BriefcaseBusiness}
              tone="success"
            />
            <KpiCard
              title={t('programs.detail.kpi.pointsPerTransaction')}
              value={program.points_per_transaction?.toLocaleString(locale) ?? t('programs.detail.kpi.pointsRuleTiers')}
              description={program.points_per_transaction === null ? t('programs.detail.kpi.pointsRuleDescriptionTiers') : t('programs.detail.kpi.pointsRuleDescriptionValue')}
              icon={HandCoins}
              tone="warning"
            />
            <KpiCard
              title={t('programs.detail.kpi.programStatus')}
              value={programStatusLabel(program.status, t)}
              description={t('programs.detail.kpi.programStatusDescription')}
              icon={Activity}
              tone="info"
            />
          </>
        ) : (
          <>
            <KpiCard
              title={t('programs.detail.assignedAgentsTitle')}
              value={formatAppNumber(program.assigned_agents_count ?? assignedAgents.length)}
              description={t('programs.detail.kpi.assignedAgentsDescription')}
              icon={Users}
              tone="primary"
            />
            <KpiCard
              title={t('programs.detail.kpi.prospects')}
              value={formatAppNumber(activeProspects.length)}
              description={t('programs.detail.kpi.prospectsDescription')}
              icon={ScanSearch}
              tone="info"
            />
            <KpiCard
              title={t('programs.detail.kpi.converted')}
              value={formatAppNumber(convertedProspects.length)}
              description={t('programs.detail.kpi.convertedDescription')}
              icon={BriefcaseBusiness}
              tone="success"
            />
            <KpiCard
              title={t('programs.detail.kpi.pointsRule')}
              value={program.points_per_transaction?.toLocaleString(locale) ?? t('programs.detail.kpi.pointsRuleTiers')}
              description={program.points_per_transaction === null ? t('programs.detail.kpi.pointsRuleDescriptionTiers') : t('programs.detail.kpi.pointsRuleDescriptionValue')}
              icon={HandCoins}
              tone="warning"
            />
          </>
        )}
      </div>

      {/* ── Program Info ── */}
      <Card className="border-0 shadow-none">
        <CardContent className="space-y-5 p-4">
          {/* Description */}
          <div>
            <h3 className="text-lg font-semibold tracking-tight">{program.name}</h3>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
              {program.description ?? t('programs.detail.overviewDescriptionFallback')}
            </p>
          </div>

          {/* Rules */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-border bg-muted/15 px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t('programs.detail.commission')}</p>
              <p className="mt-1 text-sm font-medium text-foreground">{commissionLabel(program, t)}</p>
              <p className="text-xs text-muted-foreground">{t('programs.detail.commissionHelper')}</p>
            </div>
            <div className="rounded-lg border border-border bg-muted/15 px-4 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t('programs.detail.points')}</p>
              <p className="mt-1 text-sm font-medium text-foreground">{pointsRuleLabel(program, t, locale)}</p>
              <p className="text-xs text-muted-foreground">{t('programs.detail.pointsHelper')}</p>
            </div>
          </div>

          {/* Eligibility */}
          <div className="rounded-lg border border-border bg-muted/15 px-4 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t('programs.detail.eligibility')}</p>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {program.eligibility_criteria ?? t('programs.detail.eligibilityFallback')}
            </p>
          </div>

          {/* Exchange Config */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t('programs.detail.exchangeConfig')}</p>
              <Badge variant="outline" className={cn('w-fit', exchangeConfig.badgeClass)}>
                {exchangeConfig.label}
              </Badge>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {hasCash ? (
                <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-4 py-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-800 dark:text-emerald-300">{t('programs.detail.cash')}</p>
                    <HandCoins className="size-4 text-emerald-600" />
                  </div>
                  <p className="mt-1 text-sm font-semibold text-foreground">{cashRuleLabel(program, t, locale)}</p>
                  {isOwnerActionPending ? null : (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="mt-2 h-auto px-0 py-0 text-xs text-emerald-700 hover:text-emerald-800 dark:text-emerald-300"
                      disabled={!canEditCashShortcut}
                      onClick={() => {
                        if (!canEditCashShortcut) return
                        setCashEditOpen(true)
                      }}
                    >
                      {t('programs.detail.editCash')}
                    </Button>
                  )}
                </div>
              ) : null}
              {hasRewards ? (
                <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-900 dark:text-amber-300">{t('programs.detail.rewards')}</p>
                    <Gift className="size-4 text-amber-600" />
                  </div>
                  <p className="mt-1 text-sm font-semibold text-foreground">
                    {program.exchange_pack?.name ?? t('programs.detail.noPackLinked')}
                  </p>
                  <div className="mt-2 space-y-1">
                    {program.exchange_pack?.items.length ? (
                      program.exchange_pack.items.slice(0, 4).map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between gap-3 rounded-md bg-background px-3 py-1.5 text-xs"
                        >
                          <span className="min-w-0 truncate font-medium text-foreground">{item.title}</span>
                          <span className="shrink-0 font-mono text-muted-foreground">
                            {formatAppNumber(item.points_cost)} {t('programs.detail.pointsSuffix')}
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="rounded-md bg-background px-3 py-1.5 text-xs text-muted-foreground">
                        {t('programs.detail.noRewardItems')}
                      </p>
                    )}
                  </div>
                  {isOwnerActionPending ? null : (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="mt-2 h-auto px-0 py-0 text-xs text-amber-700 hover:text-amber-800 dark:text-amber-300"
                      disabled={!canEditRewardsShortcut}
                      onClick={() => {
                        if (!canEditRewardsShortcut) return
                        setRewardsEditOpen(true)
                      }}
                    >
                      {t('programs.detail.manageRewards')}
                    </Button>
                  )}
                </div>
              ) : null}
            </div>
          </div>

          {/* Dates */}
          <div className="flex flex-wrap gap-2">
            <MetaBadge label={t('programs.detail.activated')} value={formatAppDate(program.activated_at)} />
            <MetaBadge label={t('programs.detail.updated')} value={formatAppDate(program.updated_at)} />
          </div>
        </CardContent>
      </Card>

      {!isAgentView ? (
        <Card className="border-0 shadow-none">
          <CardContent className="p-4">
            <DashboardSectionHeader
              title={t('programs.detail.assignedAgentsTitle')}
            />
            <div className="mb-3">
              <label className="sr-only" htmlFor="program-agents-search">
                {t('programs.detail.searchAssignedAgents')}
              </label>
              <div className="relative max-w-sm">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="program-agents-search"
                  value={agentTableSearch}
                  onChange={(event) => {
                    setAgentTableSearch(event.target.value)
                    setAgentTablePage(1)
                  }}
                  placeholder={t('programs.detail.searchAssignedAgentsPlaceholder')}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="overflow-hidden rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 text-center">{t('programs.detail.numberColumn')}</TableHead>
                    <TableHead>{t('programs.detail.agentColumn')}</TableHead>
                    <TableHead className="hidden sm:table-cell">{t('programs.detail.emailColumn')}</TableHead>
                    <TableHead className="hidden md:table-cell">{t('programs.detail.codeColumn')}</TableHead>
                    <TableHead>{t('programs.detail.statusColumn')}</TableHead>
                    <TableHead className="hidden lg:table-cell">{t('programs.detail.assignedAt')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagedAssignedAgents.length > 0 ? (
                    pagedAssignedAgents.map((assignment, index) => {
                      const agent = assignment.agent!
                      const displayName = agent.display_name?.trim() || agent.email || t('programs.detail.unknownAgent')
                      return (
                        <TableRow key={assignment.assignment_id}>
                          <TableCell className="text-center text-muted-foreground">
                            {(agentPageSafe - 1) * agentTablePageSize + index + 1}
                          </TableCell>
                          <TableCell>
                            <Link
                              to={`/agents/${agent.id}`}
                              className="group -m-1 flex min-w-0 items-center gap-2.5 rounded-md p-1 text-left outline-none transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            >
                              <Avatar className="size-9 shrink-0">
                                <AvatarImage src={agent.avatar_url ?? undefined} alt={displayName} />
                                <AgentAvatarFallback seed={avatarSeedForUser(agent)} className="text-xs font-medium">
                                  {agentInitials(agent.display_name, agent.email)}
                                </AgentAvatarFallback>
                              </Avatar>
                              <div className="min-w-0">
                                <p className="truncate font-medium text-primary underline-offset-2 group-hover:underline">
                                  {displayName}
                                </p>
                                <p className="truncate text-[11px] text-muted-foreground sm:hidden">
                                  {agent.email ?? t('programs.detail.emailUnavailable')}
                                </p>
                              </div>
                            </Link>
                          </TableCell>
                          <TableCell className="hidden max-w-[14rem] truncate sm:table-cell">
                            {agent.email ?? t('programs.detail.noEmail')}
                          </TableCell>
                          <TableCell className="hidden font-mono text-xs text-muted-foreground md:table-cell">
                            {agent.agent_code ?? t('programs.detail.noCode')}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="w-fit capitalize">
                              {t(`programs.detail.assignmentStatus.${assignment.status}`, { defaultValue: t('common.unknown') })}
                            </Badge>
                          </TableCell>
                          <TableCell className="hidden text-sm text-muted-foreground lg:table-cell">
                            {formatAppDate(assignment.assigned_at)}
                          </TableCell>
                        </TableRow>
                      )
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <EmptyState
                          message={
                            filteredAssignedAgents.length === 0 && assignedAgents.length > 0
                              ? t('programs.detail.assignedAgentsEmptyFiltered')
                              : t('programs.detail.assignedAgentsEmpty')
                          }
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePaginationBar
                page={agentPageSafe}
                pageSize={agentTablePageSize}
                totalItems={filteredAssignedAgents.length}
                onPageChange={setAgentTablePage}
                onPageSizeChange={setAgentTablePageSize}
                pageSizeOptions={[10, 25, 50]}
              />
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card className="border-0 shadow-none">
        <CardContent className="p-4">
          <DashboardSectionHeader
            title={t('programs.detail.programProspectsTitle')}
          />
          {canViewProspects ? (
            <div className="mb-3">
              <label className="sr-only" htmlFor="program-prospects-search">
                {t('programs.detail.searchProspects')}
              </label>
              <div className="relative max-w-sm">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="program-prospects-search"
                  value={prospectTableSearch}
                  onChange={(event) => {
                    setProspectTableSearch(event.target.value)
                    setProspectTablePage(1)
                  }}
                  placeholder={t('programs.detail.searchProspectsPlaceholder')}
                  className="pl-9"
                />
              </div>
            </div>
          ) : null}
          {!canViewProspects ? (
            <EmptyState message={t('programs.detail.prospectsRoleUnavailable')} />
          ) : prospectsQuery.isPending ? (
            <Skeleton className="h-72 rounded-lg" />
          ) : prospectsQuery.isError ? (
            <article className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700">
              {(prospectsQuery.error as ApiError).message}
            </article>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 text-center">{t('programs.detail.numberColumn')}</TableHead>
                    <TableHead>{t('programs.detail.contactColumn')}</TableHead>
                    <TableHead className="hidden sm:table-cell">{t('programs.detail.emailColumn')}</TableHead>
                    {!isAgentView ? (
                      <TableHead className="hidden md:table-cell">{t('programs.detail.agentColumn')}</TableHead>
                    ) : null}
                    <TableHead>{t('programs.detail.pipelineColumn')}</TableHead>
                    {!isAgentView ? (
                      <TableHead>{t('programs.detail.syncColumn')}</TableHead>
                    ) : null}
                    <TableHead className="hidden lg:table-cell">{t('programs.detail.submittedColumn')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagedProgramProspects.length > 0 ? (
                    pagedProgramProspects.map((prospect, index) => {
                      const pipeline = prospectPipelinePresentation(prospect, t)
                      return (
                        <TableRow key={prospect.id} className={prospect.deleted_at ? 'opacity-70' : undefined}>
                          <TableCell className="text-center text-muted-foreground">
                            {(prospectPageSafe - 1) * prospectTablePageSize + index + 1}
                          </TableCell>
                          <TableCell>
                            <Link
                              to={buildProspectDetailPath({
                                prospectId: prospect.id,
                              })}
                              className="group -m-1 block min-w-0 rounded-md p-1 text-left outline-none transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            >
                              <p className="truncate font-medium text-primary underline-offset-2 group-hover:underline">
                                {prospect.contact_name}
                              </p>
                              <p className="truncate text-[11px] text-muted-foreground">
                                {prospect.company_name ?? t('programs.detail.companyFallback')}
                              </p>
                            </Link>
                          </TableCell>
                          <TableCell className="hidden max-w-[14rem] truncate sm:table-cell">
                            {prospect.contact_email ?? t('programs.detail.emailUnavailable')}
                          </TableCell>
                          {!isAgentView ? (
                            <TableCell className="hidden max-w-[12rem] truncate text-muted-foreground md:table-cell">
                              {prospect.agent_id ? (
                                <Link to={`/agents/${prospect.agent_id}`} className="text-primary underline-offset-2 hover:underline">
                                  {prospect.agent_name ?? t('programs.detail.agentUnknownFallback')}
                                </Link>
                              ) : (
                                prospect.agent_name ?? t('programs.detail.agentUnknownFallback')
                              )}
                            </TableCell>
                          ) : null}
                          <TableCell>
                            <Badge variant="outline" className={cn('w-fit text-xs', pipeline.className)}>
                              {pipeline.label}
                            </Badge>
                          </TableCell>
                          {!isAgentView ? (
                            <TableCell>
                              <Badge variant="outline" className={cn('w-fit text-xs', submissionBadgeClass(prospect.submission_status))}>
                                {submissionLabel(prospect.submission_status, t)}
                              </Badge>
                            </TableCell>
                          ) : null}
                          <TableCell className="hidden text-sm text-muted-foreground lg:table-cell">
                            {formatAppDateTime(prospect.submitted_at)}
                          </TableCell>
                        </TableRow>
                      )
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={isAgentView ? 5 : 7}>
                        <EmptyState
                          message={
                            filteredProgramProspects.length === 0 && programProspects.length > 0
                              ? t('programs.detail.prospectsEmptyFiltered')
                              : t('programs.detail.prospectsEmpty')
                          }
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePaginationBar
                page={prospectPageSafe}
                pageSize={prospectTablePageSize}
                totalItems={filteredProgramProspects.length}
                onPageChange={setProspectTablePage}
                onPageSizeChange={setProspectTablePageSize}
                pageSizeOptions={[10, 25, 50]}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <ProgramFormDialog
        open={editOpen}
        title={t('programs.detail.editTitle')}
        submitLabel={t('common.save')}
        packs={packsQuery.data?.data ?? []}
        initialProgram={program}
        isSubmitting={updateMutation.isPending}
        error={updateProgramError}
        onClose={() => {
          setEditOpen(false)
          updateMutation.reset()
        }}
        onSubmit={async (payload) => {
          await updateMutation.mutateAsync({ payload })
        }}
      />

      <ProgramCashRulesDialog
        open={cashEditOpen}
        program={program}
        isSubmitting={updateMutation.isPending}
        error={updateProgramError}
        onClose={() => {
          setCashEditOpen(false)
          updateMutation.reset()
        }}
        onSubmit={async (pointsPerEuro) => {
          await updateMutation.mutateAsync({
            payload: toProgramPayload(program, {
              points_per_euro: pointsPerEuro,
            }),
          })
        }}
      />

      <ProgramRewardPackDialog
        open={rewardsEditOpen}
        program={program}
        packs={rewardPacks}
        isSubmitting={updateMutation.isPending}
        error={updateProgramError}
        onClose={() => {
          setRewardsEditOpen(false)
          updateMutation.reset()
        }}
        onSubmit={async (exchangePackId) => {
          await updateMutation.mutateAsync({
            payload: toProgramPayload(program, {
              exchange_pack_id: exchangePackId,
            }),
          })
        }}
      />

      <AddProspectMethodDialog
        open={addProspectMethodOpen}
        programId={program.id}
        agentCode={user?.agent_profile?.agent_code ?? ''}
        onClose={() => setAddProspectMethodOpen(false)}
        isSubmittingForm={createProspectMutation.isPending}
        formError={createProspectError}
        onFormSubmit={async (payload) => {
          try { await createProspectMutation.mutateAsync(payload) } catch { /* error surfaced via formError */ }
        }}
      />


      <ProgramAssignmentDialog
        open={Boolean(assignDialogProgram)}
        program={assignDialogProgram}
        agents={agentsQuery.data?.data ?? []}
        assignments={assignmentQuery.data?.data ?? []}
        isSubmitting={syncAssignmentsMutation.isPending}
        error={(syncAssignmentsMutation.error as ApiError | null) ?? null}
        onClose={() => {
          setAssignDialogProgram(null)
          syncAssignmentsMutation.reset()
        }}
        onSubmit={async (agentIds) => {
          if (!assignDialogProgram) return
          await syncAssignmentsMutation.mutateAsync({
            nextProgramId: assignDialogProgram.id,
            agentIds,
          })
        }}
      />

      <ProgramLifecycleConfirmDialog
        action={pendingOwnerAction}
        isSubmitting={isOwnerActionPending}
        onClose={() => setPendingOwnerAction(null)}
        onConfirm={async (type, nextProgram) => {
          if (type === 'activate') {
            await activateMutation.mutateAsync(nextProgram.id)
          } else if (type === 'pause') {
            await pauseMutation.mutateAsync(nextProgram.id)
          } else if (type === 'reactivate' || type === 'lift_suspension') {
            await reactivateMutation.mutateAsync(nextProgram.id)
          } else if (type === 'suspend') {
            await suspendMutation.mutateAsync(nextProgram.id)
          } else if (type === 'archive') {
            await archiveMutation.mutateAsync(nextProgram.id)
          } else if (type === 'delete') {
            await deleteArchivedMutation.mutateAsync(nextProgram.id)
          }
          setPendingOwnerAction(null)
        }}
      />


    </section>
  )
}

function MetaBadge({ label, value }: { label: string; value: string }) {
  return (
    <Badge variant="outline" className="gap-1.5 bg-muted/30 px-2.5 py-1 text-xs font-medium">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground">{value}</span>
    </Badge>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <p className="rounded-lg border border-dashed border-border bg-muted/15 px-4 py-5 text-sm text-muted-foreground">
      {message}
    </p>
  )
}
